import React, { useEffect } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import type { PlayerDiedEvent, PlayerRespawnedEvent } from '../../../shared/types/NetworkMessages';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import { VFXSystem } from '../../effects/VFXSystem';
import { showSpellText } from '../../debug/DebugSpellText';
import type { RemotePlayerManager } from '../network/RemotePlayerManager';
import { showsCastBar } from '../combat/castBarAbility';
import { OverheadLayout } from '../../rendering/overhead/OverheadLayout';
import { remoteCastNameFloat, remoteCastNameText } from './remoteCastName';

/**
 * RemotePlayerVfxBridge — passive React component that adds visual
 * feedback for *remote* player actions (bots + other humans) so the
 * spectator view doesn't feel dead. The local player's own ability /
 * damage / death VFX is handled by the existing combat-side path; this
 * fills the gap for everyone else's actions.
 *
 * What it draws on which event:
 *
 *   EVENTS.WIRE_PLAYER_DIED     → death burst + "💀" floating text on
 *                                  the victim's sprite. Mild screen
 *                                  shake only when the local player
 *                                  made the kill (a local victim's
 *                                  death is DeathOverlay's path).
 *
 *   EVENTS.WIRE_PLAYER_RESPAWNED → respawn flash + "✨" floating text on
 *                                  the respawning sprite. Diagnostic
 *                                  log line emitted unconditionally so
 *                                  we can verify server is sending the
 *                                  message for the local sid (current
 *                                  unresolved bug).
 *
 *   EVENTS.ABILITY_CASTED       → cast flash + ability-name floating
 *                                  text on the caster's sprite. Skip
 *                                  the local player (their own cast
 *                                  VFX is owned by their Ability impl
 *                                  in src/game/systems/player/abilities).
 *                                  Skip projectile abilities — those
 *                                  already get a visual via
 *                                  EntitySystem.handleRemoteAbilityProjectile.
 *
 *   (EVENTS.PLAYER_DAMAGED handler removed 2026-07-16, VQA sprint
 *   U-cross-client-2: DamagePresentationService owns ALL victim FCT —
 *   local and remote — via EVENTS.DAMAGE_APPLIED; rendering here too
 *   double-printed every PvP/bot hit on every observing client.)
 *
 * The local-player VFX paths are deliberately NOT touched — overlap
 * would double-show effects. The bridge's filter is "skip if sid
 * matches local sessionId" for the per-actor effects.
 */

const KILL_SCREENSHAKE_INTENSITY = 2;
const KILL_SCREENSHAKE_DURATION_MS = 200;

function getLocalSid(): string | null {
  const w = (typeof window === 'undefined' ? null : window) as any;
  return w?.nm?.getRoom?.()?.sessionId ?? null;
}

function getRemotePlayerManager(): RemotePlayerManager | null {
  // RemotePlayerManager is OWNED by EntitySystem (not registered as a
  // top-level service). EntitySystem itself is registered via the
  // game-kernel registerSystem path, so we go through it.
  const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
  return entitySystem?.getRemotePlayerManager?.() ?? null;
}

function getRemoteSpritePos(sid: string): { x: number; y: number } | null {
  const sprite = getRemotePlayerManager()?.getSprite?.(sid);
  if (!sprite) return null;
  return { x: sprite.x ?? 0, y: sprite.y ?? 0 };
}

function getLocalSpritePos(): { x: number; y: number } | null {
  const cam = ServiceRegistry.getInstance().get('CameraSystem');
  const sprite = cam?.getManager().getPlayerSprite();
  if (!sprite) return null;
  return { x: sprite.x ?? 0, y: sprite.y ?? 0 };
}

function getSpritePos(sid: string): { x: number; y: number } | null {
  const localSid = getLocalSid();
  if (localSid && sid === localSid) return getLocalSpritePos();
  return getRemoteSpritePos(sid);
}

function getVfx(): VFXSystem | undefined {
  // VFXSystem is a singleton — never registered with ServiceRegistry.
  // After the scene has called VFXSystem.getInstance(scene) once, the
  // no-arg form is safe and returns the live instance.
  try {
    return VFXSystem.getInstance();
  } catch {
    return undefined;
  }
}

const RemotePlayerVfxBridgeInner: React.FC = () => {
  useEffect(() => {
    const bus = EventBus.getInstance();

    // Death VFX — fires for ANY player, skipping local (DeathOverlay
    // owns that path).
    const onPlayerDied = (msg: PlayerDiedEvent) => {
      const sid = msg?.victimSid;
      if (!sid) return;
      const localSid = getLocalSid();
      const isLocal = !!localSid && sid === localSid;
      const pos = getSpritePos(sid);
      if (!pos) return;
      const vfx = getVfx();
      if (!vfx) return;

      vfx.showFloatingText('💀', pos.x, pos.y - 30, {
        text: '💀',
        color: '#e74c3c',
        fontSize: '28px',
        duration: 1500,
        offsetY: -10,
        style: 'damage',
        fade: true,
      });

      // Light screen shake for a kill the LOCAL player made. Other players'
      // deaths at someone else's hand shake no screen — bot duels across
      // /brawl shook every client (lane AP, effects/screenFeelScope.ts).
      if (!isLocal && !!localSid && msg.killerSid === localSid) {
        vfx.screenShake(KILL_SCREENSHAKE_INTENSITY, KILL_SCREENSHAKE_DURATION_MS);
      }
    };

    // Respawn VFX + diagnostic log. The diagnostic is the killer here:
    // we currently believe the local player gets stuck on death (death
    // overlay shows but never clears). This log line tells us
    // unambiguously whether the server emitted the wire event for the
    // local sid.
    const onPlayerRespawned = (msg: PlayerRespawnedEvent) => {
      const sid = msg?.victimSid;
      if (!sid) return;
      const localSid = getLocalSid();
      const isLocal = !!localSid && sid === localSid;
      gameLogger.info(
        'state',
        `✨ [RemoteVfx] playerRespawned received sid=${sid} local=${isLocal} health=${msg?.health}`,
      );
      const pos = getSpritePos(sid);
      const vfx = getVfx();
      if (!pos || !vfx) return;
      vfx.showFloatingText('✨ Respawn', pos.x, pos.y - 30, {
        text: '✨ Respawn',
        color: '#3498db',
        fontSize: '14px',
        duration: 1200,
        offsetY: -20,
        style: 'ability',
        fade: true,
      });
    };

    // Ability cast VFX for *remote* casters. Skip projectile abilities
    // (those already get a visual via EntitySystem) and skip the local
    // player (their ability impl owns the cast VFX).
    const onAbilityCasted = (msg: any) => {
      const sid = msg?.playerId;
      if ((msg?.ability_id ?? msg?.abilityId) === 'assassin_auto') return;
      const abilityName = remoteCastNameText(msg);
      if (!sid || !abilityName) return;
      const localSid = getLocalSid();
      if (localSid && sid === localSid) return; // local owns its own VFX
      const pos = getRemoteSpritePos(sid);
      if (!pos) return;
      const vfx = getVfx();
      if (!vfx) return;
      // A cast-bar ability's name is on the caster's bar already (plan 15 §2.5).
      // The rest float above the caster's overhead group, not on its bars.
      const float = remoteCastNameFloat(abilityName, pos, OverheadLayout.get().groupTop(sid));
      if (!showsCastBar(msg?.ability_id ?? msg?.abilityId)) vfx.showFloatingText(float.text, float.x, float.y, {
        text: float.text, color: '#9b59b6', fontSize: '12px', duration: 800, offsetY: 0, style: 'ability', fade: true,
      });
      // Debug overlay: prominent spell text (gated by ?debugSpellText=1)
      const scene = getRemotePlayerManager()?.getSprite?.(sid)?.scene;
      if (scene) {
        showSpellText(scene, abilityName, pos.x, float.y - 20);
      }
    };

    // Victim FCT deliberately NOT rendered here: DamagePresentationService
    // owns all damage numbers (local AND remote victims) via
    // EVENTS.DAMAGE_APPLIED — a second render off the same broadcast
    // double-printed every PvP/bot hit (VQA sprint U-cross-client-2).

    bus.on(EVENTS.WIRE_PLAYER_DIED, onPlayerDied as (msg: unknown) => void);
    bus.on(EVENTS.WIRE_PLAYER_RESPAWNED, onPlayerRespawned as (msg: unknown) => void);
    bus.on(EVENTS.ABILITY_CASTED, onAbilityCasted as any);

    return () => {
      bus.off(EVENTS.WIRE_PLAYER_DIED, onPlayerDied as (msg: unknown) => void);
      bus.off(EVENTS.WIRE_PLAYER_RESPAWNED, onPlayerRespawned as (msg: unknown) => void);
      bus.off(EVENTS.ABILITY_CASTED, onAbilityCasted as any);
    };
  }, []);

  return null;
};

/** Public wrapper — always mounts (no preset gate). The handlers
 *  themselves filter local-vs-remote so there's nothing to gate at
 *  the component layer; spectators and participants alike benefit
 *  from these visuals. */
export const RemotePlayerVfxBridge: React.FC = () => {
  return <RemotePlayerVfxBridgeInner />;
};
